<template>
    <section class="rulebook-cycles">
        <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-3">
            <div>
                <h5 class="mb-1">자동 실행 이력</h5>
                <p class="text-muted mb-0">
                    최근 50회차입니다. 매일 03:00 (KST) 회차가 피드백을 모아 판단 기준 후보를 만듭니다.
                </p>
            </div>
            <b-button size="sm"
                      variant="outline-secondary"
                      class="mt-2 mt-md-0"
                      :disabled="loading"
                      @click="loadCycles"
            >
                <b-spinner v-if="loading" small class="mr-1" />새로고침
            </b-button>
        </div>

        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="loadCycles">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading && cycles.length === 0" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />실행 이력을 불러오는 중입니다.
        </div>

        <b-table v-else
                 :items="cycles"
                 :fields="fields"
                 responsive
                 hover
                 :show-empty="!error"
                 class="cycle-table"
                 empty-text="아직 실행된 회차가 없습니다."
        >
            <template #cell(createdAt)="row">
                {{ formatDate(row.item.createdAt) }}
            </template>
            <template #cell(status)="row">
                <b-badge :variant="statusMeta(row.item).variant">{{ statusMeta(row.item).label }}</b-badge>
            </template>
            <template #cell(decision)="row">
                {{ decisionLabel(row.item) }}
            </template>
            <template #cell(sourceCount)="row">
                {{ row.item.sourceCount }}
            </template>
            <template #cell(dispositions)="row">
                <span v-for="key in dispositionKeys" :key="key" class="disposition">
                    {{ dispositionLabel(key) }} {{ row.item.dispositions[key] }}
                </span>
            </template>
            <template #cell(version)="row">
                {{ versionLabel(row.item) }}
                <b-badge v-if="row.item.version"
                         :variant="versionStatusMeta(row.item.version.status).variant"
                         class="ml-1"
                >
                    {{ versionStatusMeta(row.item.version.status).label }}
                </b-badge>
            </template>
            <template #cell(errorCode)="row">
                {{ errorCodeLabel(row.item) }}
            </template>
            <template #cell(actions)="row">
                <b-button size="sm" variant="outline-secondary" @click="row.toggleDetails">
                    {{ row.detailsShowing ? '접기' : '상세' }}
                </b-button>
            </template>
            <template #row-details="row">
                <div class="cycle-detail">
                    <div class="detail-field">
                        <span class="field-label">편집 판단 근거</span>
                        <pre v-if="hasText(row.item.reasoning)"
                             class="text-block"
                             v-text="row.item.reasoning"
                        />
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                    <div class="detail-field">
                        <span class="field-label">리뷰 결과</span>
                        <template v-if="row.item.review">
                            <div class="mb-1">
                                <b-badge :variant="reviewMeta(row.item).variant">
                                    {{ reviewMeta(row.item).label }}
                                </b-badge>
                            </div>
                            <pre v-if="hasText(row.item.review.reason)"
                                 class="text-block"
                                 v-text="row.item.review.reason"
                            />
                        </template>
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                    <div class="detail-field">
                        <span class="field-label">안전 점검</span>
                        <pre v-if="hasText(row.item.safetyFindings)"
                             class="text-block"
                             v-text="row.item.safetyFindings"
                        />
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                </div>
            </template>
        </b-table>
    </section>
</template>

<script>
import {
    DISPOSITION_KEYS,
    apiErrorMessage,
    cycleStatusMeta,
    decisionMeta,
    formatKstDateTime,
    normalizeCycleList,
    reviewVerdictMeta,
    versionStatusMeta,
} from './rulebook-presentation';

const CYCLE_LIMIT = 50;
const EMPTY_LABEL = '—';

export default {
    name: 'RulebookCyclesTab',
    data() {
        return {
            loading: false,
            error: '',
            cycles: [],
            dispositionKeys: DISPOSITION_KEYS,
            fields: [
                {key: 'createdAt', label: '시각(KST)'},
                {key: 'status', label: '상태'},
                {key: 'decision', label: '결정'},
                {key: 'sourceCount', label: '소스'},
                {key: 'dispositions', label: '처분'},
                {key: 'version', label: '생성 버전'},
                {key: 'errorCode', label: '오류 코드'},
                {key: 'actions', label: '동작'},
            ],
        };
    },
    created() {
        this.loadCycles();
    },
    methods: {
        formatDate(value) {
            return formatKstDateTime(value);
        },
        statusMeta(cycle) {
            return cycleStatusMeta(cycle.status);
        },
        versionStatusMeta,
        decisionLabel(cycle) {
            return decisionMeta(cycle.decision).label;
        },
        dispositionLabel(key) {
            return decisionMeta(key).label;
        },
        reviewMeta(cycle) {
            return reviewVerdictMeta(cycle.review ? cycle.review.verdict : '');
        },
        versionLabel(cycle) {
            if (!cycle.version) return EMPTY_LABEL;
            return cycle.version.versionNo === null ? `#${cycle.version.id}` : `v${cycle.version.versionNo}`;
        },
        errorCodeLabel(cycle) {
            return cycle.errorCode || EMPTY_LABEL;
        },
        /** 값이 있을 때만 본문 박스를 그린다. 없으면 대시 하나만 보인다. */
        hasText(value) {
            return typeof value === 'string' && value.trim() !== '';
        },
        async loadCycles() {
            if (this.loading) return;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await this.$axios.get('/eval-prompt/rulebook/cycles', {
                    params: {limit: CYCLE_LIMIT},
                });
                this.cycles = normalizeCycleList(data).cycles;
            } catch (err) {
                // 재조회 실패는 직전 이력을 지우지 않는다.
                this.error = apiErrorMessage(err, '실행 이력을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.field-label {
    color: #52606d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

::v-deep .cycle-table th {
    background: #f8f9fa;
    color: #52606d;
    font-size: 0.8rem;
    white-space: nowrap;
}

::v-deep .cycle-table td {
    vertical-align: middle;
}

.disposition {
    color: #52606d;
    font-size: 0.82rem;
    margin-right: 0.6rem;
    white-space: nowrap;
}

.cycle-detail {
    background: #fafbfc;
    border-radius: 6px;
    padding: 1rem;
}

.detail-field + .detail-field {
    margin-top: 1rem;
}

.text-block {
    background: #fff;
    border: 1px solid #e5e9ee;
    border-radius: 6px;
    color: #354052;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0.35rem 0 0;
    max-height: 16rem;
    overflow: auto;
    padding: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
